import {
  createAuthenticatedLanguagePreferencesRepository,
} from "./account/language-preferences-repository.js";
import {
  browserAccountStateSessionRepository,
} from "./account/account-state-session-repository.js";

const REPOSITORY_VERSION = 1;
const REPOSITORY_BRAND = Symbol.for(
  "fishystuff.language-preference-repository.v1",
);
const LOCAL_REPOSITORY_FACTORY_SLOT = Symbol.for(
  "fishystuff.language-preference-local-repository-factory.v1",
);
const DEFAULT_PREFERENCES = Object.freeze({
  locale: null,
  dataLanguage: null,
  serverDataspace: null,
});

function hasMethods(value, names) {
  return names.every((name) => typeof value?.[name] === "function");
}

function statesEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function freezeState(value) {
  const next = { ...value };
  if (next.authoritative) {
    next.authoritative = Object.freeze({ ...next.authoritative });
  }
  if (next.legacyCleanup) {
    next.legacyCleanup = Object.freeze({ ...next.legacyCleanup });
  }
  return Object.freeze(next);
}

function boundaryState(remoteState) {
  const kind = typeof remoteState?.kind === "string"
    ? remoteState.kind
    : "uninitialized";
  return freezeState({
    kind,
    ...(remoteState?.operation ? { operation: remoteState.operation } : {}),
    ...(remoteState?.reason ? { reason: remoteState.reason } : {}),
  });
}

function projectionState(projection) {
  if (projection?.kind === "missing") {
    return freezeState({ kind: "missing" });
  }
  if (
    projection?.kind !== "ready"
    || !Number.isSafeInteger(projection.revision)
    || projection.revision <= 0
    || !projection.values
  ) {
    throw new TypeError("Account language projection is invalid.");
  }
  return freezeState({
    kind: "ready",
    revision: projection.revision,
    locale: projection.values.locale,
    dataLanguage: projection.values.dataLanguage,
    serverDataspace: projection.values.serverDataspace,
  });
}

function pendingState(remoteState) {
  return freezeState({
    kind: "pending",
    operation: remoteState.operation,
    authoritative: projectionState(remoteState.authoritativeState),
  });
}

function conflictState(remoteState) {
  const authoritative = projectionState(remoteState.authoritativeState);
  return freezeState({
    kind: "conflict",
    operation: remoteState.operation,
    expectedRevision: remoteState.expectedRevision,
    actualRevision: authoritative.kind === "ready"
      ? authoritative.revision
      : null,
    ...(authoritative.kind === "ready"
      ? {
        locale: authoritative.locale,
        dataLanguage: authoritative.dataLanguage,
        serverDataspace: authoritative.serverDataspace,
      }
      : DEFAULT_PREFERENCES),
    authoritative,
  });
}

function decisionFor(state) {
  let authoritative = state;
  if (state.kind === "pending" && state.authoritative) {
    authoritative = state.authoritative;
  } else if (state.kind === "conflict" && state.authoritative) {
    authoritative = state.authoritative;
  }

  if (authoritative.kind === "ready") {
    return freezeState({
      repositoryKind: state.kind,
      revision: authoritative.revision,
      locale: authoritative.locale,
      dataLanguage: authoritative.dataLanguage,
      serverDataspace: authoritative.serverDataspace,
      canChange: state.kind === "ready",
    });
  }
  if (authoritative.kind === "missing") {
    return freezeState({
      repositoryKind: state.kind,
      revision: null,
      ...DEFAULT_PREFERENCES,
      canChange: state.kind === "missing",
    });
  }
  if (state.kind === "conflict") {
    return freezeState({
      repositoryKind: "conflict",
      revision: state.actualRevision ?? null,
      locale: state.locale ?? null,
      dataLanguage: state.dataLanguage ?? null,
      serverDataspace: state.serverDataspace ?? null,
      canChange: false,
    });
  }
  return freezeState({
    repositoryKind: state.kind,
    revision: null,
    ...DEFAULT_PREFERENCES,
    canChange: false,
  });
}

function requireLocalRepository(repository) {
  if (
    !hasMethods(repository, [
      "dispose",
      "load",
      "reset",
      "setDataLanguage",
      "setLocale",
      "setPreferences",
      "setServerDataspace",
      "state",
      "subscribe",
    ])
  ) {
    throw new TypeError(
      "Language preferences require the exact local repository contract.",
    );
  }
  return repository;
}

export function createAccountAwareLanguagePreferenceRepository({
  localRepositoryFactory,
  authenticatedRepository,
} = {}) {
  if (typeof localRepositoryFactory !== "function") {
    throw new TypeError("Language preferences require a local repository factory.");
  }
  if (
    !hasMethods(authenticatedRepository, [
      "load",
      "reset",
      "setDataLanguage",
      "setLocale",
      "setPreferences",
      "setServerDataspace",
      "state",
      "subscribe",
    ])
  ) {
    throw new TypeError(
      "Language preferences require the authenticated repository contract.",
    );
  }

  let currentState = freezeState({ kind: "uninitialized" });
  let authority = "unknown";
  let localRepository = null;
  let unsubscribeLocal = null;
  let disposed = false;
  const listeners = new Set();

  function state() {
    return currentState;
  }

  function decision() {
    return decisionFor(currentState);
  }

  function deliver(listener) {
    try {
      listener(currentState);
    } catch (_error) {
      globalThis.reportError?.(new Error("Language preference subscriber failed."));
    }
  }

  function publish(nextState) {
    if (disposed) return currentState;
    const frozen = freezeState(nextState);
    if (statesEqual(currentState, frozen)) return currentState;
    currentState = frozen;
    for (const listener of listeners) deliver(listener);
    return currentState;
  }

  function releaseLocal() {
    if (!localRepository) return;
    const repository = localRepository;
    authority = "unknown";
    localRepository = null;
    unsubscribeLocal?.();
    unsubscribeLocal = null;
    repository.dispose();
  }

  function createLocalRepository() {
    return requireLocalRepository(localRepositoryFactory());
  }

  function activateLocal() {
    if (disposed || (authority === "local" && localRepository)) return state();
    releaseLocal();
    let repository = null;
    try {
      repository = createLocalRepository();
      const loaded = repository.load();
      authority = "local";
      localRepository = repository;
      unsubscribeLocal = repository.subscribe((nextState) => {
        if (!disposed && authority === "local" && repository === localRepository) {
          publish(nextState);
        }
      });
      return publish(loaded ?? repository.state());
    } catch (_error) {
      repository?.dispose?.();
      authority = "unknown";
      localRepository = null;
      unsubscribeLocal = null;
      return publish({
        kind: "unavailable",
        operation: "load",
        reason: "local-language-load-failed",
      });
    }
  }

  function projectAuthenticated(remoteState) {
    if (disposed) return;
    if (remoteState?.kind === "signedOut") {
      activateLocal();
      return;
    }

    releaseLocal();
    try {
      if (remoteState?.kind === "ready") {
        authority = "account";
        publish(projectionState(remoteState.authoritativeState));
        return;
      }
      if (remoteState?.kind === "pending") {
        authority = "account";
        publish(pendingState(remoteState));
        return;
      }
      if (remoteState?.kind === "conflict") {
        authority = "account";
        publish(conflictState(remoteState));
        return;
      }
      authority = "unknown";
      publish(boundaryState(remoteState));
    } catch (_error) {
      authority = "unknown";
      publish({
        kind: "unavailable",
        operation: "load",
        reason: "account-language-state-invalid",
      });
    }
  }

  const unsubscribeAuthenticated = authenticatedRepository.subscribe(
    projectAuthenticated,
  );

  async function load() {
    if (disposed) return state();
    await authenticatedRepository.load();
    return state();
  }

  async function routeCommand(method, args) {
    if (disposed) return state();
    const repository = authority === "local"
      ? localRepository
      : authority === "account" ? authenticatedRepository : null;
    if (!repository) return state();
    await repository[method](...args);
    return state();
  }

  function setPreferences(preferences, options) {
    return routeCommand("setPreferences", [preferences, options]);
  }

  function setLocale(locale, options) {
    return routeCommand("setLocale", [locale, options]);
  }

  function setDataLanguage(dataLanguage, options) {
    return routeCommand("setDataLanguage", [dataLanguage, options]);
  }

  function setServerDataspace(serverDataspace, options) {
    return routeCommand("setServerDataspace", [serverDataspace, options]);
  }

  function reset(options) {
    return routeCommand("reset", [options]);
  }

  async function resetLocal(options) {
    if (disposed) {
      return freezeState({
        kind: "unavailable",
        operation: "reset",
        reason: "language-repository-disposed",
      });
    }
    if (authority === "local" && localRepository) {
      return localRepository.reset(options);
    }

    const temporary = createLocalRepository();
    try {
      return await temporary.reset(options);
    } finally {
      temporary.dispose();
    }
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      throw new TypeError("Language preference listener must be a function.");
    }
    if (disposed) return () => {};
    listeners.add(listener);
    deliver(listener);
    return () => listeners.delete(listener);
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    unsubscribeAuthenticated();
    releaseLocal();
    listeners.clear();
  }

  return Object.freeze({
    [REPOSITORY_BRAND]: REPOSITORY_VERSION,
    decision,
    dispose,
    load,
    reset,
    resetLocal,
    setDataLanguage,
    setLocale,
    setPreferences,
    setServerDataspace,
    state,
    subscribe,
  });
}

let browserOwner = null;
let browserAuthenticatedOwner = null;
let browserLocalResetAdapter = null;
let browserLocalResetWindow = null;
let browserWindow = null;

function browserWindowRef() {
  const windowRef = globalThis.window;
  if (!windowRef || typeof windowRef !== "object") {
    throw new TypeError("Browser language preferences require a window.");
  }
  return windowRef;
}

function disposeBrowserOwner() {
  browserOwner?.dispose();
  browserAuthenticatedOwner?.dispose();
  browserOwner = null;
  browserAuthenticatedOwner = null;
}

function browserLocalRepositoryFactory(windowRef) {
  const factory = windowRef[LOCAL_REPOSITORY_FACTORY_SLOT];
  if (typeof factory !== "function") {
    throw new TypeError(
      "Browser language preferences require the local repository factory.",
    );
  }
  return factory;
}

export function browserLanguagePreferenceRepository() {
  const windowRef = browserWindowRef();
  if (browserOwner && browserWindow !== windowRef) {
    disposeBrowserOwner();
  }
  if (!browserOwner) {
    const localRepositoryFactory = browserLocalRepositoryFactory(windowRef);
    browserAuthenticatedOwner =
      createAuthenticatedLanguagePreferencesRepository({
        sessionRepository: browserAccountStateSessionRepository(),
        apiBaseUrl: windowRef.__fishystuffApiBaseUrl,
        languageConfig: windowRef.__fishystuffLanguageConfigV1,
      });
    browserOwner = createAccountAwareLanguagePreferenceRepository({
      localRepositoryFactory,
      authenticatedRepository: browserAuthenticatedOwner,
    });
    browserWindow = windowRef;
    Object.defineProperty(
      windowRef,
      "__fishystuffLanguagePreferenceRepository",
      {
        configurable: true,
        value: browserOwner,
        writable: false,
      },
    );
  }
  return browserOwner;
}

export function browserLocalLanguagePreferenceRepository() {
  const windowRef = browserWindowRef();
  if (!browserLocalResetAdapter || browserLocalResetWindow !== windowRef) {
    const localRepositoryFactory = browserLocalRepositoryFactory(windowRef);
    browserLocalResetWindow = windowRef;
    browserLocalResetAdapter = Object.freeze({
      async reset(options) {
        if (browserOwner && browserWindow === windowRef) {
          return browserOwner.resetLocal(options);
        }
        const temporary = requireLocalRepository(localRepositoryFactory());
        try {
          return await temporary.reset(options);
        } finally {
          temporary.dispose();
        }
      },
    });
  }
  return browserLocalResetAdapter;
}

if (globalThis.window?.document) {
  browserLanguagePreferenceRepository();
}
